import { apiClient } from "./apiClient";

/**
 * Баланс отдельного аккаунта (из /v2/balance)
 */
export interface AccountBalanceData {
  uuid: string;
  name: string;
  account_uid: string;
  account_type: string | null;

  // General balance
  equity: string; // Полная стоимость портфеля
  available_balance: string; // Доступный баланс портфеля
  frozen_balance: string; // Замороженный баланс портфеля
  unrealized_pnl: string; // Нереализованный PnL
  margin_ratio: string | null; // Коэффициент маржи

  // USDT balance
  usdt_equity: string; // Капитал USDT
  usdt_available_balance: string; // Доступный баланс USDT
  usdt_frozen_balance: string; // Замороженный баланс USDT
  usdt_cash_balance: string; // Cash баланс USDT
}

/**
 * Ответ с балансами пользователя
 */
export interface BalanceResponse {
  // General balance
  total_equity: string; // Сумма полных стоимостей портфелей
  total_available_balance: string; // Сумма доступных балансов портфелей
  total_frozen_balance: string; // Сумма замороженных балансов портфелей
  total_unrealized_pnl: string; // Сумма нереализованного PnL
  total_margin_ratio: string; // Сумма коэффициентов маржи

  // USDT balance
  total_usdt_equity: string; // Сумма капиталов USDT
  total_usdt_available_balance: string; // Сумма доступных балансов USDT
  total_usdt_frozen_balance: string; // Сумма замороженных балансов USDT
  total_usdt_cash_balance: string; // Сумма cash балансов USDT

  accounts: AccountBalanceData[];
}

interface LogParams {
  limit?: number;
  startTime?: string;
  endTime?: string;
  level?: string;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
}

interface SummaryStats {
  date: string; // ISO8601 string
  profit_usdt_sum: number;
  profit_percentage_sum: number;
  positions_count: number;

  // Stats by instrument type
  spot_profit: number;
  futures_profit: number;
  spot_profit_percentage: number;
  futures_profit_percentage: number;
  spot_used_fuel: number;
  futures_used_fuel: number;
  total_deals: number;

  // Balances
  spot_balance: number;
  futures_balance: number;
}

export const analyticsApi = {
  // Get balance (v2 API)
  getBalance: async (): Promise<BalanceResponse> => {
    try {
      const response = await apiClient.get("/v2/balance");
      return response.data;
    } catch (error) {
      console.error("Ошибка при получении баланса:", error);
      throw error;
    }
  },

  // Get worker logs
  getWorkerLogs: async (workerId: string, params: LogParams = {}): Promise<LogEntry[]> => {
    try {
      const queryParams = new URLSearchParams({
        limit: params.limit?.toString() || "100",
        ...(params.startTime && { start_time: params.startTime }),
        ...(params.endTime && { end_time: params.endTime }),
        ...(params.level && { level: params.level }),
      });

      const response = await apiClient.get(`/v1/analytics/logs/${workerId}?${queryParams}`);
      return response.data.logs;
    } catch (error) {
      console.error("Ошибка при получении логов:", error);
      throw error;
    }
  },

  // Get summary stats for period
  getSummaryStats: async (
    startDate: string,
    endDate: string,
    accountUuid?: string
  ): Promise<SummaryStats> => {
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });

      if (accountUuid) {
        params.append("account_uuid", accountUuid);
      }

      const response = await apiClient.get(`/v1/analytics/summary-stats?${params}`);
      return response.data;
    } catch (error) {
      console.error("Ошибка при получении сводной статистики:", error);
      throw error;
    }
  },
};
